import type { SliderTypeProps } from '@/components/slider-type/slider-type.types';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';
import Cards from '@/components/slider-type/components/cards';
import Certifications from '@/components/certifications';
import Congratulation from '@/components/slider-type/components/congratulation';
import DateField from '@/components/slider-type/components/date-field';
import Dropdown from '@/components/slider-type/components/dropdown';
import ErrorMessage from '@/components/slider-type/components/error-message';
import Header from '@/components/slider-type/components/header';
import Navigation from '@/components/slider-type/components/navigation';
import Paragraph from '@/components/slider-type/components/paragraph';
import Subline from '@/components/slider-type/components/subline';
import Text from '@/components/slider-type/components/text';
import Textarea from '@/components/slider-type/components/textarea';
import UserForm from '@/components/slider-type/components/user-form';
import { Context } from '@/components/slider-type/context';
import { SLIDE_ANIMATION_MS } from '@/components/my-slider/my-slider';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { GeneralSlice } from '@/redux/general';
import { SliderSlice } from '@/redux/slider';
import { handOver, isAutofocusSlide, isPrimed } from '@/utils/autofocus';
import '@/components/slider-type/slider-type-v1.css';
import '@/components/slider-type/slider-type-v2.css';

export default function SliderType(props: SliderTypeProps) {
  const dispatch = useAppDispatch();
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const autofocusInput = useAppSelector(ConfigSlice.selectors.autofocusInput);
  const isCurrent = useAppSelector((state) => SliderSlice.selectors.currentId(state) === props.index);
  const ref = useRef<HTMLDivElement>(null);
  const { height } = useResizeObserver({ ref, box: 'border-box' });

  const renderQuestion = (): ReactNode => {
    switch (props.slide.type) {
      case 'text':
        return <Text />;
      case 'textarea':
        return <Textarea />;
      case 'paragraph':
        return <Paragraph />;
      case 'multiple-text':
      case 'multiple':
        return <Cards />;
      case 'datefield':
        return <DateField />;
      case 'dropdown':
        return <Dropdown />;
      case 'submission':
        return <UserForm />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (props.slide.type !== 'submission') return;
    dispatch(GeneralSlice.actions.setUserFormHeight(height || 0));
  }, [props.index, height]);

  // Primed by the navigation handler; also runs on mount, since a path change can mount the arriving slide.
  useEffect(() => {
    if (!autofocusInput || !isCurrent || !isAutofocusSlide(props.slide) || !isPrimed()) return;
    const timeout = setTimeout(() => handOver(ref.current), SLIDE_ANIMATION_MS);
    return () => clearTimeout(timeout);
  }, [autofocusInput, isCurrent, props.slide]);

  return (
    <Context.Provider
      value={{
        slide: props.slide,
        index: props.index,
        onPrevious: props.prevSlide,
        onNext: props.nextSlide,
        onSubmit: props.onSubmit,
      }}
    >
      <div data-css-version={cssVersion} data-slider-type={props.slide.type} ref={ref} className='slider-type'>
        {props.slide.type === 'submission' ? (
          <>
            <Congratulation />
            {renderQuestion()}
            <div className='slider-type__certifications'>
              <Certifications />
            </div>
          </>
        ) : (
          <>
            <Header />
            {renderQuestion()}
            <ErrorMessage />
            <Subline />
            <div className='slider-type__navigation'>
              <Navigation />
            </div>
          </>
        )}
      </div>
    </Context.Provider>
  );
}
